import anime from 'animejs/lib/anime.es.js'
import { AnimationMixer, BoxBufferGeometry, Clock, sRGBEncoding, Mesh, Object3D, Vector3, ShaderMaterial, MeshBasicMaterial, SphereBufferGeometry, Color, DoubleSide } from 'three'
import { loadFBXMAIN } from '../Loaders/loadFBXCached'
import { loadGLTF } from '../Loaders/loadGLTF'
import { Howl } from 'howler'
import { SkeletonUtils } from 'three/examples/jsm/utils/SkeletonUtils.js'

let swatSounds = {
  alrightThen: new Howl({
    volume: 2.0,
    src: [require('file-loader!./sounds/alright-then.m4a')]
  }),
  sir: new Howl({
    volume: 2.0,
    src: [require('file-loader!./sounds/sir.m4a')]
  }),
  ready: new Howl({
    volume: 2.0,
    src: [require('file-loader!./sounds/ready2go.m4a')]
  }),
  hmm: new Howl({
    volume: 2.0,
    src: [require('file-loader!./sounds/hmmhmm.m4a')]
  }),
  walk: new Howl({
    volume: 2.0,
    src: [require('file-loader!./sounds/walk.m4a')]
  }),
  yea: new Howl({
    volume: 2.0,
    src: [require('file-loader!./sounds/yea.m4a')]
  }),
  running: new Howl({
    rate: 1.1,
    volume: 0.12,
    loop: true,
    src: [require('file-loader!./sounds/running.m4a')]
  })
}

const mixChar = ({ scope, char }) => {
  if (char === 'swat') {
    scope.charURL = require('!file-loader!./chars/swat.glb')
    scope.sounds = swatSounds

    scope.onSelectSounds = [
      scope.sounds.hmm,
      scope.sounds.yea,
      scope.sounds.sir,
      scope.sounds.ready
    ]

    scope.onActionSound = [
      scope.sounds.hmm,
      scope.sounds.alrightThen,
      scope.sounds.yea
    ]
  }
}

export const ModelCache = new Map()

export const getAsyncFreshGLTF = async (url) => {
  if (ModelCache.has(url)) {
    // console.log(url)
    return ModelCache.get(url)
  } else {
    let item = await loadGLTF(url)
    let clonedScene = SkeletonUtils.clone(item.scene)
    ModelCache.set(url, {
      ...item,
      scene: clonedScene
    })
    return item
  }
}

export const getAsyncFreshFBX = async (url) => {
  return await loadFBXMAIN(url)
}

export class GoTarget {
  constructor ({ ctx }) {
    let goIcon = new Object3D()
    let goMat1 = new MeshBasicMaterial({ envMap: ctx.chroma.out.envMap, color: new Color('#ffffff'), side: DoubleSide, opacity: 0.0, transparent: true, wireframe: false })
    let goMat2 = new MeshBasicMaterial({ envMap: ctx.chroma.out.envMap, color: new Color('#ffffff'), side: DoubleSide, opacity: 0.0, transparent: true, wireframe: false })
    let goMat3 = new MeshBasicMaterial({ envMap: ctx.chroma.out.envMap, color: new Color('#ffffff'), side: DoubleSide, opacity: 0.0, transparent: true, wireframe: false })

    let goGeo1 = new SphereBufferGeometry(2 * 13, 32, 32, 6, 1.6, 4, 1.8)
    let goMesh1 = new Mesh(goGeo1, goMat1)
    goIcon.add(goMesh1)

    let goGeo2 = new SphereBufferGeometry(2 * 12, 32, 32, 3, 2.1, 4, 0.7)
    let goMesh2 = new Mesh(goGeo2, goMat2)
    goIcon.add(goMesh2)

    let goGeo3 = new SphereBufferGeometry(2 * 11, 32, 32, 4, 2.1, 1, 0.6)
    let goMesh3 = new Mesh(goGeo3, goMat3)
    goIcon.add(goMesh3)

    let taller = 19.3
    goIcon.position.y += taller

    ctx.onLoop(() => {
      goMesh1.rotation.y += 0.1
      goMesh2.rotation.y -= 0.1
      goMesh3.rotation.y += 0.1
    })

    goIcon.frustumCulled = false
    goMesh1.frustumCulled = false
    goMesh2.frustumCulled = false
    goMesh3.frustumCulled = false

    ctx.scene.add(goIcon)

    let fadeOut = () => {
      anime({
        targets: [
          goMesh1.material,
          goMesh2.material,
          goMesh3.material
        ],
        opacity: 0.0
      })
    }
    let fadeIn = () => {
      anime({
        targets: [
          goMesh1.material,
          goMesh2.material,
          goMesh3.material
        ],
        opacity: 0.7
      })
    }

    // anime

    ctx.root.events.on('arrived-marker', () => {
      // goIcon.visible = false
      fadeOut()
    })
    ctx.root.events.on('start-running', ({ position }) => {
      // goIcon.visible = true
      goIcon.position.copy(position)
      goIcon.position.y += taller
      fadeIn()
    })
    // ctx.root.events.on('is-running', () => {
    //   // goIcon.visible = true
    // })
  }
}

export class PhysicsCapsule {
  constructor ({ ctx, oScale = 1.0, copyCat, runningThreshold = 25 }) {
    let size = {
      x: oScale * 1,
      y: oScale * 1.85,
      z: oScale * 1,
    }

    let cGeo = new BoxBufferGeometry(size.x, size.y, size.z, 2, 2, 2)
    // cGeo.translate(0, -size.y, 0.0)
    // cGeo.scale(oScale, oScale, oScale)

    let cMat = new ShaderMaterial({
      transparent: true,
      fragmentShader: `
        void main (void) {
          discard;
        }
      `
    })

    // cMat = new MeshBasicMaterial({ transparent: true, opacity: 0.5, color: 0xbabaff })

    let capsule = new Mesh(cGeo, cMat)
    capsule.userData.capsule = {
      alwaysUp: true,
      x: size.x * 0.5,
      y: size.y * 0.5 + size.x * 3
    }

    capsule.position.y = 1.3 * oScale
    if (ctx.services.factory && ctx.services.factory.birthPlace) {
      capsule.position.x = ctx.services.factory.birthPlace[0]
      capsule.position.z = ctx.services.factory.birthPlace[2]
      // ctx.camera.position.x += ctx.services.factory.birthPlace[0]
      // ctx.camera.position.z += ctx.services.factory.birthPlace[2]
    }

    ctx.root.ammo.addSimpleMesh({ mesh: capsule, mass: 1.3, rootScale: 1 })
    ctx.root.ammo.setMeshPosition({ mesh: capsule, local: false, alwaysUp: true })

    ctx.scene.add(capsule)

    let destination = new Vector3()
    let canSendEvent = true

    ctx.root.events.on('click-floor', ({ position }) => {
      canSendEvent = true
      ctx.root.events.emit('start-running', { position: { x: position.x, y: position.y, z: position.z } })

      position.y = 0.0
      destination.copy(position)
    })

    let worldPos = new Vector3()
    let delta = new Vector3()

    let ammoVelocity = new ctx.root.ammo.Ammo.btVector3(0, 0, 0)

    // let ammoLinearFactor = new ctx.root.ammo.Ammo.btVector3(0, 0, 0)
    let ammoAngularFactor = new ctx.root.ammo.Ammo.btVector3(0, 0, 0)
    // var ammoStartTransform = new ctx.root.ammo.Ammo.btTransform()



    const { body } = ctx.ammo.bodyMeshMap.get(capsule.uuid)
    body.setDamping(0.95, 0.99)
    ctx.onLoop(() => {
      body.activate()
      if (destination.length() === 0.0) {
        copyCat.position.copy(capsule.position)
        copyCat.position.y -= size.y * 0.5 + oScale * 0.35
        return
      }

      capsule.getWorldPosition(worldPos)
      delta.copy(destination).sub(worldPos).normalize().multiplyScalar(oScale * 0.5)

      worldPos.y = destination.y
      let diff = worldPos.distanceTo(destination)
      if (diff >= runningThreshold) {
        body.setDamping(0.99, 0.99)
        ammoAngularFactor.setValue(0, 1, 0)
        body.setAngularFactor(ammoAngularFactor)

        ammoVelocity.setValue(1.5 * delta.x, -2, 1.5 * delta.z)
        body.applyCentralImpulse(ammoVelocity)

        ctx.root.events.emit('is-running')
      } else {
        body.setDamping(0.95, 0.99)

        if (canSendEvent) {
          canSendEvent = false
          ctx.root.events.emit('arrived-marker')
        }
      }

      copyCat.position.copy(capsule.position)
      copyCat.position.y -= size.y * 0.5 + oScale * 0.35
    })

    ctx.onClean(() => {
      ctx.root.ammo.removeMesh({ mesh: capsule })
    })

    return capsule
  }
}

export class Commander {
  constructor (props) {
    let { ctx, char = 'swat', o3d } = props
    // super({ ctx })
    // profile
    this.charURL = ''
    this.sounds = {}
    this.onSelectSounds = []
    this.onActionSound = []

    //
    this.actorScale = 16

    this.actions = {}
    this.glb = {}
    this.actorO3D = new Object3D()
    ctx.actorO3D = this.actorO3D
    o3d.add(this.actorO3D)

    mixChar({ scope: this, char })

    this.waiter = Promise.all([
      getAsyncFreshGLTF(this.charURL),
      getAsyncFreshFBX(require('!file-loader!./actions/running.fbx')),
      getAsyncFreshFBX(require('!file-loader!./actions/control-standing-idle.fbx')),
    ])
      .then(([
        actor,
        running,
        idle
      ]) => {
        this.glb.actor = actor
        this.actions.running = running
        this.actions.idle = idle
        return
      })

    this._actionTrigger = ''

    this.waiter
      .then(() => {
        return this.setupActor({ ctx, o3d })
      })
      .then(() => {
        return this.setupActions({ ctx, o3d })
      })
      .then(() => {
        return this.playAction({ name: 'idle' })
      })
      .then(async () => {
      })

    ctx.onClean(() => {
      this.onSelectSounds.forEach(e => e.stop())
      this.onActionSound.forEach(e => e.stop())
    })

    return this
  }

  wait () {
    return this.waiter
  }

  onSyncSound ({ mode }) {
    let runSound = this.sounds.running
    if (runSound) {
      if (mode === 'running') {
        runSound.fade(0, 0.07, 50)
        runSound.play()
      } else if (mode === 'idle') {
        runSound.fade(0.07, 0, 50)
        setTimeout(() => {
          runSound.stop()
        }, 50)
      }
    }
  }

  set actionTrigger (v) {
    if (this._actionTrigger !== v) {
      this.playAction({ name: v })
      this.onSyncSound({ mode: v })
    }
    this._actionTrigger = v
  }

  get actionTrigger () {
    return this._actionTrigger
  }

  async setupActor ({ ctx }) {
    this.glb.actor.scene.rotation.x = Math.PI * -0.5
    // this.glb.actor.scene.position.y = -0.5

    this.actorO3D.scale.set(this.actorScale, this.actorScale, this.actorScale)
    // this.glb.actor.scene.scale.set(this.actorScale, this.actorScale, this.actorScale)
    // this.glb.actor.scene.position.y += this.actorScale * 1

    this.glb.actor.scene.traverse((item) => {
      if (item && item.isMesh) {
        if (item.material) {
          item.material.map.encoding = sRGBEncoding
          item.castShadow = true
          // item.receiveShadow = true
          // item.material.emissive = new Color('#121212')

          if (ctx.chroma && ctx.chroma.out && ctx.chroma.out.envMap) {
            item.material.envMap = ctx.chroma.out.envMap
          }
        }
      }
    })

    this.actorO3D.add(this.glb.actor.scene)

    let cGeo = new BoxBufferGeometry(1, 1.88, 1)
    let cMat = new ShaderMaterial({
      transparent: true,
      fragmentShader: `
        void main (void) {
          discard;
        }
      `
    })
    // cMat = new MeshBasicMaterial({ color: 0xff0000, opacity: 0.75, transparent: true })
    let clicker = new Mesh(cGeo, cMat)

    let runningThreshold = 16
    new GoTarget({ ctx })
    new PhysicsCapsule({ ctx, copyCat: this.actorO3D, oScale: this.actorScale, runningThreshold })
    clicker.position.y = 1.88 * 0.5
    this.actorO3D.add(clicker)

    clicker.cursor = 'pointer'
    clicker.userData.interactive = true
    clicker.on('pointerdown', () => {
      // console.log(`onClickChar`)
      let ar = this.onSelectSounds
      let sound = ar[Math.floor(ar.length * Math.random())]
      sound.play()

      ctx.root.events.emit('select-item', 'character')
    })

    let charPos = new Vector3()
    let destination = new Vector3()
    // let delta = new Vector3()

    ctx.root.events.on('click-floor', ({ position }) => {
      // position.x += (Math.random() < 0.5 ? -1 : 1) + (Math.random() < 0.5 ? -1 : 1) * 2.0
      // position.z += (Math.random() < 0.5 ? -1 : 1) + (Math.random() < 0.5 ? -1 : 1) * 2.0
      destination.copy(position)
      destination.y = 0
    })

    ctx.onLoop(() => {
      if (destination.length() === 0.0) {
        return
      }

      // this.actorO3D.getWorldPosition(charPos)
      // charPos.y = 0

      this.actorO3D.getWorldPosition(charPos)
      charPos.y = 0
      destination.y = 0
      // delta.copy(destination).sub(charPos).normalize().multiplyScalar(1.0 / 10).multiplyScalar(this.actorScale * 0.75 * 1.5)

      let diff = charPos.distanceTo(destination)
      if (diff > runningThreshold) {
        this.actionTrigger = 'running'

        destination.y = this.actorO3D.position.y
        this.actorO3D.lookAt(destination)

        // this.actorO3D.position.add(delta)
      } else {
        this.actionTrigger = 'idle'
      }
    })
  }

  async setupActions ({ ctx }) {
    let actor = this.actorO3D

    this.mixer = new AnimationMixer(actor)
    let clock = new Clock()
    ctx.onLoop(() => {
      let dt = clock.getDelta()
      this.mixer.update(dt)
    })
  }

  async playAction ({ name }) {
    let action = await this.prepAnimation({ fbx: this.actions[name], mixer: this.mixer, inPlace: true })

    if (this.lastPlayingMove) {
      this.lastPlayingMove.fadeOut(0.2)
    }

    action.reset()
    action.play()

    this.lastPlayingMove = action
  }

  prepAnimation ({ fbx, mixer, inPlace }) {
    if ((fbx && !fbx.animations) || (!fbx)) {
      return Promise.reject(new Error('no fbx'))
    }
    return new Promise((resolve) => {
      let action = false
      fbx.animations.forEach((clip) => {
        if (inPlace) {
          if (clip.tracks[0] && clip.tracks[0].name === 'mixamorigHips.position') {
            let values = clip.tracks[0].values
            for (var i = 0; i < values.length; i += 3) {
              values[i + 0] = 0
              // values[i + 1] = values[i + 1]
              values[i + 2] = 0
            }
          }
        }

        action = mixer.clipAction(clip)
        action.duration = clip.duration
        action.mixer = mixer
        resolve(action)
      });
    })
  }
}
